import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './spareInvoicesModuleConfig';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

export interface SpareInvoiceLine {
  id?: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  unitId?: number | null;
  invoiceQty?: number | null;
  unitPrice?: string | number | null;
  lineTotal?: string | number | null;
  discount?: string | number | null;
  vat?: string | number | null;
  requisitionQty?: number | null;
  enquiryQty?: number | null;
  quotationQty?: number | null;
  deliveryQty?: number | null;
  purchaseOrderQty?: number | null;
  itemNotes?: string | null;
  item?: {
    level1?: string | null;
    level2?: string | null;
    level3?: string | null;
  } | null;
  unit?: { id?: number; ucode?: string | null; descr?: string | null } | null;
}

export interface SpareInvoiceExpense {
  id?: number;
  itemDescr?: string | null;
  value?: string | number | null;
  vat?: string | number | null;
}

export interface SpareInvoice {
  id: number;
  invDate?: string | null;
  caseId: number;
  supplierId: number;
  ref?: string | null;
  currencyId?: number | null;
  exchangeRate?: string | number | null;
  exchangeDate?: string | null;
  paymentTerms?: string | null;
  refN?: number | null;
  rcvDate?: string | null;
  dueDate?: string | null;
  purchaseOrderId?: number | null;
  deliveryId?: number | null;
  vesselId?: number | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  lineItems?: SpareInvoiceLine[];
  expenses?: SpareInvoiceExpense[];
  _count?: { lineItems: number };
  spareCase?: { id: number; ref?: string | null; caseDate?: string | null } | null;
  order?: {
    id: number;
    ref?: string | null;
    orderDate?: string | null;
    quotationId?: number | null;
    quotation?: { id: number; ref?: string | null; enqId?: number | null } | null;
  } | null;
  delivery?: { id: number; ref?: string | null; dlvDate?: string | null } | null;
  vessel?: { id: number; vesselName: string; vesselAbrv?: string | null; imoNumber?: string | null } | null;
  supplier?: { id: number; name: string; code?: string | null; email?: string | null; isActive?: boolean } | null;
  currency?: { id: number; code: string; name: string; symbol?: string | null; isActive?: boolean } | null;
}

export const useSpareInvoices = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<PaginatedApiResponse<SpareInvoice> | SpareInvoice[]>(
    `${moduleConfig.module.api.list}${q}`,
    fetcher,
    { revalidateOnFocus: true },
  );
  const normalized = normalizePaginatedListResponse<SpareInvoice>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useSpareInvoice = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<SpareInvoice>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};
